import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { PasswordPolicy } from 'types/auth';
import {
  InputField,
  PasswordInput,
  PasswordPolicyMessage,
  TokenInput,
} from 'app/components/Core';
import { AuddlySignUpFormFields } from 'app/components/AuddlySignUp/AuddlySignUpForm';

interface AuddlySignUpPasswordFormProps {
  passwordPolicy?: PasswordPolicy;
  showEmailField: boolean;
  codeErrorMessage: string;
}

export const AuddlySignUpPasswordForm: React.FC<AuddlySignUpPasswordFormProps> = ({
  passwordPolicy,
  showEmailField,
  codeErrorMessage,
}) => {
  const {
    formState: { errors },
    control,
    register,
    clearErrors,
    getValues,
    trigger,
  } = useFormContext<AuddlySignUpFormFields>();

  return (
    <VStack spacing={2}>
      <VStack mb={4}>
        <Text
          fontSize="17px"
          lineHeight="23px"
          textAlign="left"
          margin={0}
          mb={8}
        >
          Please use the code we&apos;ve sent you to verify your email address.
        </Text>
        <Controller
          control={control}
          name="code"
          render={({
            field: { onChange, value },
            fieldState: { error, isDirty },
          }) => (
            <TokenInput
              onChange={onChange}
              defaultValue={value}
              error={codeErrorMessage || (isDirty ? error?.message : '')}
            />
          )}
        />
      </VStack>
      {showEmailField && (
        <Box w="full" style={{ marginTop: '16px' }}>
          <InputField
            label="Email"
            {...register('email')}
            isInvalid={Boolean(errors.email)}
            errorMessage={errors.email?.message}
          />
        </Box>
      )}
      <Box w="full" style={{ marginTop: '16px' }}>
        <PasswordInput
          label="Password"
          autoComplete="new-password"
          {...register('password', {
            onBlur: () => {
              if (errors.password?.type === 'manual') {
                clearErrors();
              }
            },
            onChange: () => {
              if (getValues('passwordRepeat')) {
                trigger('passwordRepeat');
              }
            },
          })}
          isInvalid={Boolean(errors?.password)}
          errorMessage={errors?.password?.message}
        />
      </Box>
      <Box w="full" style={{ marginTop: '16px' }}>
        <PasswordInput
          label="Confirm Password"
          autoComplete="new-password"
          {...register('passwordRepeat', {
            onBlur: () => {
              if (errors.password?.type === 'manual') {
                clearErrors();
              }
            },
          })}
          isInvalid={Boolean(errors?.passwordRepeat)}
          errorMessage={errors?.passwordRepeat?.message}
          data-testid="confirm-password"
          errorMessageProps={{
            'data-testid': 'confirm-password-error-text',
          }}
        />
      </Box>
      <PasswordPolicyMessage passwordPolicy={passwordPolicy} />
    </VStack>
  );
};
